package com.arena_web.utils

import kotlinx.browser.window

/**
 * TikTok Pixel conversion-event helper — the TikTok twin of [MetaPixel].
 *
 * The pixel BASE code — the events.js loader, `ttq.load()`, the initial
 * `ttq.page()`, and the SPA history-navigation page hook — lives in the static
 * `<head>` (see `site/build.gradle.kts` head.add), so ad visitors who bounce
 * during load are still counted. That head snippet respects a prior explicit
 * "declined" consent and skips localhost.
 *
 * This object only fires CONVERSION events against the already-global `ttq`.
 * The head stub queues calls until events.js finishes loading, so events fired
 * early are not lost. When the pixel was suppressed (declined / localhost),
 * `window.ttq` is undefined and every method is a safe no-op.
 */
object TikTokPixel {
    /**
     * Enable TikTok Advanced Matching for the signed-in user. Passes the raw
     * email + external id to `ttq.identify` — the TikTok pixel SDK SHA-256
     * hashes PII in-browser before sending, so no plaintext leaves the page
     * (same contract as Meta's `fbq('init', …, userData)`). Safe no-op if the
     * pixel was suppressed.
     */
    fun identify(email: String?, externalId: String?) {
        val ttq = window.asDynamic().ttq
        if (ttq == null) return
        if (email.isNullOrBlank() && externalId.isNullOrBlank()) return
        val userData = js("({})")
        // TikTok matches on the SHA-256 of the LOWERCASED, trimmed email —
        // normalise before handing it to the SDK's hasher or "Ivan@Gmail.com"
        // would never match.
        email?.takeIf { it.isNotBlank() }?.let { userData.email = it.trim().lowercase() }
        externalId?.takeIf { it.isNotBlank() }?.let { userData.external_id = it }
        ttq.identify(userData)
    }

    /**
     * The user started the subscription checkout (about to be redirected to the
     * mono payment page). Standard TikTok `InitiateCheckout` event — the
     * mid-funnel optimisation signal, mirroring [MetaPixel.trackInitiateCheckout].
     * The terminal Purchase will come server-side via the TikTok Events API
     * once that is wired (phase 2, like Meta CAPI).
     */
    fun trackInitiateCheckout(value: Double, currency: String) {
        val ttq = window.asDynamic().ttq
        if (ttq == null) return
        val params = js("({})")
        params.value = value
        params.currency = currency
        ttq.track("InitiateCheckout", params)
    }

    /** Custom event: the user joined a sport event — secondary ad signal. */
    fun trackJoinEvent(eventId: String) {
        val ttq = window.asDynamic().ttq
        if (ttq == null) return
        val params = js("({})")
        params.event_id = eventId
        ttq.track("JoinEvent", params)
    }
}
